import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';
import { getActiveEvent, getRegistrations } from '../services/api';

const Home = () => {
  const videoRef = useRef(null);
  const [event, setEvent] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch event and registrations
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const activeEvent = await getActiveEvent();
        setEvent(activeEvent);
        
        if (activeEvent) {
          const registrationsData = await getRegistrations(activeEvent.id);
          setRegistrations(registrationsData || []);
        }
      } catch (err) {
        console.error('Error fetching event data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      // Hide controls programmatically (iOS Safari workaround)
      video.controls = false;
      video.setAttribute('controls', 'false');
      
      // Function to attempt playing the video
      const attemptPlay = () => {
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.log('Video autoplay failed:', error);
            // Try again after a short delay for iOS
            setTimeout(() => {
              video.play().catch(() => {});
            }, 100);
          });
        }
      };

      // iOS Safari often requires the video to be loaded before playing
      const handleLoadedData = () => {
        video.controls = false;
        attemptPlay();
      };

      const handleCanPlay = () => {
        video.controls = false;
        attemptPlay();
      };

      const handleTimeUpdate = () => {
        // Hide controls periodically (iOS sometimes shows them)
        video.controls = false;
        // Add fade effect near the end of video
        const timeRemaining = video.duration - video.currentTime;
        if (timeRemaining < 0.5 && video.duration > 0) {
          video.style.opacity = '0.3';
        } else {
          video.style.opacity = '1';
        }
      };

      const handleEnded = () => {
        video.style.opacity = '0';
        setTimeout(() => {
          video.currentTime = 0;
          video.style.opacity = '1';
          video.controls = false;
          video.play();
        }, 300);
      };

      // Try to play immediately
      attemptPlay();

      // Also try when video is loaded (important for iOS)
      video.addEventListener('loadeddata', handleLoadedData);
      video.addEventListener('canplay', handleCanPlay);
      video.addEventListener('timeupdate', handleTimeUpdate);
      video.addEventListener('ended', handleEnded);
      
      // Continuously hide controls (iOS Safari can show them briefly)
      const hideControlsInterval = setInterval(() => {
        if (video) {
          video.controls = false;
        }
      }, 100);

      // Fallback: try playing on any user interaction (iOS workaround)
      const handleUserInteraction = () => {
        if (video.paused) {
          video.controls = false;
          attemptPlay();
        }
      };
      
      document.addEventListener('touchstart', handleUserInteraction, { once: true });
      document.addEventListener('click', handleUserInteraction, { once: true });

      return () => {
        clearInterval(hideControlsInterval);
        video.removeEventListener('loadeddata', handleLoadedData);
        video.removeEventListener('canplay', handleCanPlay);
        video.removeEventListener('timeupdate', handleTimeUpdate);
        video.removeEventListener('ended', handleEnded);
        document.removeEventListener('touchstart', handleUserInteraction);
        document.removeEventListener('click', handleUserInteraction);
      };
    }
  }, []);

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-banner">
            <video 
              ref={videoRef}
              src={`${process.env.PUBLIC_URL}/Hole%201%20Flyover.mov`}
            className="hero-banner-video"
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            controls={false}
          />
        </div>
        <div className="hero-content">
          <div className="hero-main-card">
            <div className="hero-main-content">
              <div className="hero-left">
                <h1>Toke For Stroke Open</h1>
                <div className="hero-event-info">
                  <div className="hero-info-item">
                    <span className="hero-info-value">April 20, 2026</span>
                  </div>
                  <div className="hero-info-item">
                    <span className="hero-info-value">Berkshire Valley Golf Course</span>
                    <span className="hero-info-value">Oak Ridge, NJ</span>
                  </div>
                  
                </div>
                <Link to="https://teetimetony.com/products/toke-for-stroke-1-player?variant=51510043246874" className="hero-donation-button">REGISTER</Link>
              </div>
              <div className="hero-sponsors">
                <div className="hero-sponsor-image">
                  <img src={`${process.env.PUBLIC_URL}/toke-logo.png`} alt="Toke Logo" className="sponsor-img" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="event-details" className="section">
        <div className="section-content">
          <div className="event-description-section">
            <h2>Event Description</h2>
            <p className="event-description-subtitle">Get ready for one of the most laid-back, unforgettable golf outings New Jersey has ever seen. The Toke for Stroke Open is taking over Berkshire Valley Golf Course 
              on 4/20/26 for a day packed with great golf, great vibes, and even better people.</p>
          </div>

          <div className="additional-description-section">
            <h2>Additional Event Description</h2>
            <p className="additional-description-text">
            This isn’t your typical stiff, buttoned-up scramble. Expect a fun-first atmosphere where golfers of all skill levels can come out, light up the scorecard, and enjoy the game the way it’s meant to be played. From on-course contests and prizes to sponsor activations and post-round hangouts, this event is all about community, good energy, and unforgettable moments.

Hosted by TeeTimeTony, the Toke for Stroke Open, blends golf culture with personality, creativity, and a little edge. Whether you’re here to chase birdies, support sponsors, or just soak in the vibes with your foursome, you’re guaranteed a day you’ll be talking about long after the final putt drops
            </p>
          </div>

          <div id="registration" className="registration-details-section">
            <h2>Registration Details</h2>
            <div className="registration-content">
              <div className="golf-course-logo">
                <img src={`${process.env.PUBLIC_URL}/berkshire-logo.png`} alt="Berkshire Valley Country Club" className="course-logo-img" />
              </div>
              <div className="registration-info">
                <p className="event-date-location">
                  <strong><u>Monday April 20, 2026 at Berkshire Valley Golf Course</u></strong>
                </p>
                <p className="event-address">28 Cozy Lake Rd, Oak Ridge, NJ 07438</p>
                
                <div className="registration-detail-item">
                  <strong>Format:</strong> 18-hole, 4-person scramble
                </div>
                
                <div className="registration-detail-item">
                  <strong>Cost:</strong> $180/person ($720 - Foursome)
                </div>
                
                <div className="schedule-section">
                  <strong>Schedule of Events:</strong>
                  <div className="schedule-list">
                    <div className="schedule-item">
                      <h4>Arrival | 8:00 AM</h4>
                      <ul>
                        <li>Sponsor Check-In</li>
                        <li>Golfer Check-In</li>
                        <li>Breakfast</li>
                      </ul>
                    </div>
                    <div className="schedule-item">
                      <h4>Pre-Kickoff | 8:30 AM</h4>
                      <ul>
                        <li>Putting Contest</li>
                        <li>Photos</li>
                        <li>Introductory Speech</li>
                      </ul>
                    </div>
                    <div className="schedule-item">
                      <h4>Start Time | 9:30 AM</h4>
                      <ul>
                        <li>Spark Joint</li>
                        <li>Start Engines</li>
                        <li>Play Golf</li>
                      </ul>
                    </div>
                    <div className="schedule-item">
                      <h4>Post-Round</h4>
                      <ul>
                        <li>Enjoy Lunch</li>
                        <li>Award Ceremony</li>
                        <li>Raffles</li>
                        <li>Giveaway Prizes</li>
                        <li>Thank You!</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <Link to="https://teetimetony.com/products/toke-for-stroke-1-player?variant=51510043246874" className="registration-register-button">
                  REGISTER
                </Link>
                
                <div className="accepted-payments">
                  <p className="accepted-payments-label">We Accept:</p>
                  <div className="credit-card-buttons">
                    <img src={`${process.env.PUBLIC_URL}/visa.png`} alt="Visa" className="credit-card-button" />
                    <img src={`${process.env.PUBLIC_URL}/mastercard.png`} alt="Mastercard" className="credit-card-button" />
                    <img src={`${process.env.PUBLIC_URL}/discover.png`} alt="Discover" className="credit-card-button" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="sponsors" className="section">
        <div className="section-content">
          <div className="technology-sponsor-section">
            <div className="technology-sponsor-card">
              <h3 className="technology-sponsor-heading">Technology Sponsor</h3>
              <div className="technology-sponsor-card-content">
                <div className="technology-sponsor-logo">
                  <img src={`${process.env.PUBLIC_URL}/ews.jpg`} alt="Technology Sponsor Logo" className="technology-sponsor-image" />
                </div>
                <div className="technology-sponsor-content">
                  <h4 className="technology-sponsor-company">Enterprise Web Solutions</h4>
                  <p className="technology-sponsor-description">
                  Enterprise Web Solutions designs professional, mobile-friendly websites for businesses of all sizes. We combine creativity and technology to bring your brand to life online.
                  </p>
                  <a href="https://enterprisewebsolutions.co/" target="_blank" rel="noopener noreferrer" className="technology-sponsor-button">
                    SPONSOR WEBSITE
                    <span className="external-link-icon">↗</span>
                  </a>
                </div>
              </div>
            </div>
          </div>

          <h2>Sponsors</h2>
          <p>
            We are grateful to our sponsors for their continued support of our golf
            tournaments and events.
          </p>

          <div className="sponsors-grid">
            <div className="sponsor-card">
              <a 
                href="https://highdrivesgolf.com/?srsltid=AfmBOooULVOHmn88FtYG8g6auEAxZEPTIiFTVdy5JsZyfMXVFkCwccZs" 
                target="_blank" 
                rel="noopener noreferrer"
                className="sponsor-link"
              >
                <img src={`${process.env.PUBLIC_URL}/highdrives.png`} alt="High Drives Golf" className="sponsor-card-image" />
              </a>
            </div>
            <div className="sponsor-card">
              <a 
                href="https://www.releafnewton.com/location/releafnewton" 
                target="_blank" 
                rel="noopener noreferrer"
                className="sponsor-link"
              >
                <img src={`${process.env.PUBLIC_URL}/releafnewton.png`} alt="Releaf Newton" className="sponsor-card-image" />
              </a>
            </div>
            <div className="sponsor-card">
              <a 
                href="https://www.instagram.com/suburbancertified?igsh=MXc4aXVlNjVxaWlxeA==" 
                target="_blank" 
                rel="noopener noreferrer"
                className="sponsor-link"
              >
                <img src={`${process.env.PUBLIC_URL}/suburbancertified.png`} alt="Suburban Certified" className="sponsor-card-image" />
              </a>
            </div>
            <div className="sponsor-card">
              <a 
                href="https://www.greenlightnj.com/location/greenlightnj" 
                target="_blank" 
                rel="noopener noreferrer"
                className="sponsor-link"
              >
                <img src={`${process.env.PUBLIC_URL}/greenlight.jpg`} alt="Greenlight" className="sponsor-card-image" />
              </a>
            </div>
            <div className="sponsor-card">
              <div className="sponsor-link">
                <img src={`${process.env.PUBLIC_URL}/king-palm.png`} alt="King Palm" className="sponsor-card-image" />
              </div>
            </div>
            <div className="sponsor-card">
              <div className="sponsor-link">
                <img src={`${process.env.PUBLIC_URL}/north-lake-supply.png`} alt="North Lake Supply" className="sponsor-card-image" />
              </div>
            </div>
            <div className="sponsor-card">
              <div className="sponsor-link">
                <img src={`${process.env.PUBLIC_URL}/for-the-love-of-green.png`} alt="For the Love of Green" className="sponsor-card-image" />
              </div>
            </div>
            <div className="sponsor-card">
              <div className="sponsor-link">
                <img src={`${process.env.PUBLIC_URL}/digital-mulligans.png`} alt="Digital Mulligans" className="sponsor-card-image" />
              </div>
            </div>
            <div className="sponsor-card">
              <div className="sponsor-link">
                <img src={`${process.env.PUBLIC_URL}/wed-smoke.png`} alt="We'd Smoke" className="sponsor-card-image" />
              </div>
            </div>
            <div className="sponsor-card">
              <div className="sponsor-link">
                <img src={`${process.env.PUBLIC_URL}/rove.png`} alt="Rove" className="sponsor-card-image" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="registrants" className="section">
        <div className="section-content">
          <h2>Registrants</h2>
          <p>See who's participating in this year's event.</p>
          {loading ? (
            <div className="registrants-preview centered">
              <p>Loading registration information...</p>
            </div>
          ) : (
            <>
              <div className="registrants-status">
                <div className="registrants-status-info">
                  <span className="registrants-count">
                    {registrations.reduce((sum, reg) => sum + (reg.numberOfPlayers || 0), 0)}
                  </span>
                  <span className="registrants-total">
                    / {event?.maxSpots || 100}
                  </span>
                </div>
                <div className="registrants-progress-bar">
                  <div 
                    className="registrants-progress-fill" 
                    style={{ 
                      width: `${Math.min(
                        (registrations.reduce((sum, reg) => sum + (reg.numberOfPlayers || 0), 0) / (event?.maxSpots || 100)) * 100,
                        100
                      )}%` 
                    }}
                  ></div>
                </div>
              </div>
              <Link to="https://teetimetony.com/products/toke-for-stroke-1-player?variant=51510043246874" className="registration-register-button">
                REGISTER
              </Link>
              <div className={`registrants-preview ${registrations.length === 0 ? 'centered' : ''}`}>
                {registrations.length === 0 ? (
                  <p>No registrations yet. Be the first to register!</p>
                ) : (
                  <div className="registrants-list">
                    {registrations.map((reg) => (
                      <div key={reg.id} className="registrant-card">
                        <div className="registrant-card-content">
                          <div className="registrant-main-info">
                            <h4 className="registrant-name">{reg.contactName}</h4>
                            {reg.teamName && (
                              <p className="registrant-team">{reg.teamName}</p>
                            )}
                          </div>
                          {reg.playerNames && reg.playerNames.length > 0 && (
                            <div className="registrant-player-names">
                              {reg.playerNames.map((name, idx) => (
                                <span key={idx} className="player-name-tag">{name}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </section>

    </div>
  );
};

export default Home;

